import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { CheckCircle } from 'lucide-react';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const Billing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSubscribe = async (tier) => {
    try {
      await axios.post(`${API_BASE}/employer/subscribe`, {
        user_id: user.id,
        tier
      });
      toast.success(`Subscribed to ${tier} plan!`);
      setTimeout(() => navigate('/employer/dashboard'), 1500);
    } catch (error) {
      toast.error('Subscription failed');
    }
  };

  const plans = [
    {
      name: 'Starter',
      price: '€199',
      period: '/month',
      features: [
        'Up to 3 active roles',
        'Access to all candidates',
        'Basic analytics',
        'Email support'
      ]
    },
    {
      name: 'Growth',
      price: '€499',
      period: '/month',
      features: [
        'Up to 10 active roles',
        'Priority candidate access',
        'Advanced analytics',
        'Priority support',
        'Custom matching weights'
      ],
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      features: [
        'Unlimited roles',
        'Dedicated account manager',
        'API access',
        'Custom integrations',
        'White-label options'
      ]
    }
  ];

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Choose Your Plan</h1>
          <p className="text-lg text-slate-600">Select the plan that best fits your hiring needs</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`bg-white border rounded-2xl p-8 relative ${
                plan.popular
                  ? 'border-[#0D9488] ring-2 ring-[#0D9488]/20 shadow-lg'
                  : 'border-slate-200'
              }`}
              data-testid={`plan-${plan.name.toLowerCase()}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#0D9488] text-white px-4 py-1 rounded-full text-sm font-semibold">
                  Most Popular
                </div>
              )}
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{plan.name}</h3>
              <div className="mb-6">
                <span className="text-4xl font-bold">{plan.price}</span>
                <span className="text-slate-500">{plan.period}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start text-sm">
                    <CheckCircle className="w-5 h-5 text-[#0D9488] mr-2 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => handleSubscribe(plan.name.toLowerCase())}
                className={`w-full rounded-full ${
                  plan.popular
                    ? 'bg-[#0D9488] hover:bg-[#115E59] text-white'
                    : 'bg-white border border-slate-200 text-slate-900 hover:bg-slate-50'
                }`}
                data-testid={`subscribe-${plan.name.toLowerCase()}-btn`}
              >
                {plan.name === 'Enterprise' ? 'Contact Sales' : 'Subscribe'}
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-slate-50 border border-slate-200 rounded-2xl p-8 text-center">
          <p className="text-sm text-slate-600">This is a demo subscription. No payment will be processed.</p>
        </div>
      </div>
    </div>
  );
};

export default Billing;
